// src/pages/ArtistProfileSetupPage.js
import React, { useState } from 'react';
import { http } from "../lib/axios";
import { geocodeAddress } from '../lib/geocode';
import { Container, Typography, TextField, Button, Select, MenuItem, InputLabel, FormControl, OutlinedInput, Chip, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AgreementAcceptModal from "../components/AgreementAcceptModal";
import { getAgreementStatus } from "../lib/apiAgreements";
import { ART_TYPE_OPTIONS } from '../utils/hostProfileOptions';
import ContextHelpButton from '../components/ContextHelpButton';

export default function ArtistProfileSetupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    about: '',
    exhibitions: '',
    recognition: '',
    portfolio_link: '',
    address: '',
    art_preferences: [],
  });
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const hasAcceptedArtistAgreement = async () => {
    try {
      const res = await getAgreementStatus(["ARTIST_PLATFORM"]);
      const row = res.results?.find(item => item.code === "ARTIST_PLATFORM");
      return !!row?.accepted;
    } catch (error) {
      console.error('Failed to check artist agreement status:', error);
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleArtPreferencesChange = (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, art_preferences: typeof value === 'string' ? value.split(',') : value }));
  };

  const getCoordinatesFromAddress = async (address) => {
    const data = await geocodeAddress(address);
    if (data.results && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry;
      return { latitude: lat, longitude: lng };
    } else {
      throw new Error('Unable to geocode address.');
    }
  };

  const getCurrentUserId = async () => {
    const { data } = await http.get('/api/accounts/me/');
    return data.id;
  };

  const fetchExistingArtist = async (userId) => {
    const artistRes = await http.get(`/artists/?user=${userId}`);
    return artistRes.data.length > 0 ? artistRes.data[0] : null;
  };

  const handleFinalSubmit = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No access token.');

      // 1) who is the user?
      const userId = await getCurrentUserId();

      // 2) prevent duplicate profile
      const existingArtist = await fetchExistingArtist(userId);
      if (existingArtist) {
        alert('You already have an Artist profile. Redirecting you to your dashboard.');
        navigate('/dashboard/artist');
        return;
      }

      // 3) geocode
      const coords = await getCoordinatesFromAddress(form.address);

      // 4) create including required user field
      await http.post('/artists/', {
        user: { id: userId },
        about: form.about,
        exhibitions: form.exhibitions,
        recognition: form.recognition,
        portfolio_link: form.portfolio_link,
        art_preferences: form.art_preferences,
        address: form.address,
        location: coords,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate('/dashboard/artist');
    } catch (error) {
      const detail = error?.response?.data || error.message;
      console.error('Failed to create artist profile:', detail);
      alert(`Profile creation failed. ${typeof detail === 'string' ? detail : 'Please check your entries and try again.'}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const accepted = await hasAcceptedArtistAgreement();
    if (!accepted) {
      setPendingSubmit(true);
      setShowAgreementModal(true);
      return;
    }
    await handleFinalSubmit();
  };

  return (
    <Container sx={{ mt: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>Setup Your Artist Profile</Typography>
        <ContextHelpButton section="artist-help" title="Help for artist profile setup" />
      </Box>
      <form onSubmit={handleSubmit}>
        <TextField fullWidth label="About" name="about" multiline rows={4} onChange={handleChange} margin="normal" />
        <TextField fullWidth label="Exhibitions" name="exhibitions" multiline rows={3} onChange={handleChange} margin="normal" />
        <TextField fullWidth label="Recognitions" name="recognition" multiline rows={3} onChange={handleChange} margin="normal" />
        <TextField fullWidth label="Portfolio Link" name="portfolio_link" onChange={handleChange} margin="normal" />
        <TextField fullWidth label="Address" name="address" onChange={handleChange} margin="normal" />
        <FormControl fullWidth margin="normal">
          <InputLabel>Art Preferences</InputLabel>
          <Select
            multiple
            name="art_preferences"
            value={form.art_preferences}
            onChange={handleArtPreferencesChange}
            input={<OutlinedInput label="Art Preferences" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((val) => (<Chip key={val} label={val} />))}
              </Box>
            )}
          >
            {ART_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" variant="contained" sx={{ mt: 2 }}>Create Profile</Button>
      </form>
      <AgreementAcceptModal
        open={showAgreementModal}
        onClose={() => {
          setShowAgreementModal(false);
          setPendingSubmit(false);
        }}
        agreementCode="ARTIST_PLATFORM"
        title="Artist Platform Agreement"
        workflowContext={{ workflow: "artist_onboarding", promo_code: "EARLYARTIST6_5PCT_PLATFORM" }}
        onAccepted={async () => {
          setShowAgreementModal(false);
          if (pendingSubmit) {
            setPendingSubmit(false);
            await handleFinalSubmit();
          }
        }}
      />
    </Container>
  );
}
